<template>
    <pc-capsule
        v-show="shouldShow"
        v-bind="capsuleBinder"
        v-bind:title="capsuleTitle"
        v-bind:has-body="hasBody"
        has-table
        data-test-role="esignatures-capsule"
        v-on="capsuleListeners">
        <template v-slot:capsule-table>
            <pc-tabs
                hide-single
                hide-border
                v-bind:text-capitalize="false">
                <pc-tab
                    v-bind:is-shown="hasLegacyEnvelopes"
                    v-bind:title="legacyEnvelopeTitle">
                    <legacy-capsule-table
                        v-bind:e-signatures="eSignatures"
                        v-bind:is-admin-page="isAdminPage"
                        v-bind:wide-view="wideView"
                        v-bind:is-read-only-user="isReadOnlyUser"
                        v-bind:initial-sort="initialSort"
                        v-on:signature-update="onSignatureUpdated"
                        v-on:resend-notifications="resendNotifications" />
                </pc-tab>
                <pc-tab
                    v-bind:is-shown="hasAdvancedEnvelopes"
                    v-bind:title="advancedEnvelopeTitle">
                    <advanced-capsule-table
                        v-bind:envelopes="v2Envelopes"
                        v-bind:is-admin-page="isAdminPage"
                        v-bind:wide-view="wideView"
                        v-bind:is-read-only-user="isReadOnlyUser"
                        v-bind:initial-sort="initialSort"
                        v-bind:program-id="ProgramId"
                        v-bind:project-id="ProjectId"
                        v-bind:content-library-items-by-id="contentLibraryItemsById"
                        v-bind:provider-capabilities="v2ProviderCapabilities"
                        v-on:envelope-updated="onV2EnvelopeUpdated"
                        v-on:envelope-voided="onV2EnvelopeVoided" />
                </pc-tab>
            </pc-tabs>
        </template>
        <template v-slot:default>
            <div>
                <div
                    v-if="showRoleDifferenceMessage"
                    class="text-primary">
                    <span class="fw-bold">*</span>
                    <span class="fst-italic">
                        {{ roleDifferenceMessage }}
                    </span>
                </div>
            </div>
        </template>
    </pc-capsule>
</template>
<script>
    import capsuleBase from "../_sharedCapsuleBase";
    import legacyCapsuleTable from "./Legacy/legacyESignatureCapsuleTable.vue";
    import advancedCapsuleTable from "./Advanced/advancedESignatureCapsuleTable.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/eSignaturesCapsule.i18n";

    // Initial sort by Document Names column (effectively by first document
    // in list)
    const initialSort = [[1, "desc"]];

    export default {
        components: {
            legacyCapsuleTable,
            advancedCapsuleTable
        },
        extends: capsuleBase,
        props: {
            id: { type: String, required: false, default: "e-signatures" },
            title: { type: String, required: false, default: "eSignature Statuses" },
            model: { type: Object, required: true },
            projectOwner: { type: Object, required: false, default: null } // non-admin only
        },
        data() {
            return {
                eSignatures: [],
                isLoginDifferentFromOwnerRole: false,
                recipientsBeingEdited: [],
                initialSort: initialSort,
                v2Envelopes: [],
                contentLibraryItemsById: {}
            };
        },
        computed: {
            showRoleDifferenceMessage() {
                return (
                    !this.isAdminPage &&
                    this.isLoginDifferentFromOwnerRole &&
                    this.eSignatures.some((es) => {
                        return es.IsInvisibleToProjectOwner;
                    })
                );
            },
            roleDifferenceMessage() {
                if (!this.showRoleDifferenceMessage) {
                    return null;
                }
                return this.visibilityPermissionText;
            },
            shouldShow() {
                return this.hasLegacyEnvelopes || this.hasAdvancedEnvelopes;
            },
            hasBody() {
                return this.showRoleDifferenceMessage;
            },
            controller() {
                return this.$serviceControllers.ESignatures;
            },
            hasAdvancedEnvelopes() {
                return this.v2Envelopes?.length > 0;
            },
            hasLegacyEnvelopes() {
                return this.eSignatures?.length > 0;
            },
            programHasAnyV2Envelopes() {
                return this.model.HasAnyV2Envelopes;
            },
            v2ProviderCapabilities() {
                return this.model.ESignatureProviderCapabilities;
            },
            ...mapI18n(
                {
                    ...KEYS,
                    visibilityPermissionText() {
                        return {
                            key: KEYS.visibilityPermissionText,
                            values: { projectOwnerEmail: this.projectOwner.Email }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            projectOwner() {
                if (!this.isAdminPage) {
                    // Reload eSignatures when project owner changes
                    this.loadLegacyESignatureStatuses();
                }
            }
        },
        created() {
            this.loadLegacyESignatureStatuses();
            this.loadV2EnvelopeStatuses();
        },
        methods: {
            async loadLegacyESignatureStatuses() {
                if (this.isAdminPage) {
                    const result = await this.controller.GetESignaturesForProject({
                        programPublicId: this.ProgramId,
                        projectPublicId: this.ProjectId
                    });
                    this.eSignatures = result.Data;
                } else {
                    const result = await this.controller.GetESignatureStatuses({
                        programPublicId: this.ProgramId,
                        projectPublicId: this.ProjectId,
                        workflowStatusPublicId: this.model.WorkflowStatusId
                    });
                    this.eSignatures = result.Data.eSignatureList;
                    this.isLoginDifferentFromOwnerRole = result.Data.isloginDifferentFromOwnerRole;
                }
            },
            onSignatureUpdated(eSignature, updatedESignature) {
                // Remove the original signature object from the list
                // and add the updated copy in its place

                // TODO: This works but can cause the order of the items in the table
                // to change if there's not a good way to sort them.

                this.eSignatures = this.eSignatures.filter((es) => {
                    return es.EnvelopeId !== eSignature.EnvelopeId;
                });
                this.eSignatures.push(updatedESignature);
            },
            async resendNotifications(eSignature) {
                this.$app.blockUi(this.resendingText);
                const envelopeId = eSignature.EnvelopeId;

                // Don't send updates for recipients who have completed
                const corrections = eSignature.Recipients.filter((r) => {
                    return !r.Status.IsReady;
                });

                try {
                    await this.controller.ResendEnvelopeNotificationEmails({
                        programId: this.ProgramId,
                        projectId: this.ProjectId,
                        envelopeId,
                        corrections
                    });
                    this.$app.reloadWindow();
                } finally {
                    this.$app.unBlockUi();
                }
            },
            async loadV2EnvelopeStatuses() {
                // Don't make this unnecessary call if the whole program has no v2 envelopes; it
                // does some work even if there are no envelopes for the project
                if (!this.programHasAnyV2Envelopes) {
                    return;
                }

                const result = await this.controller.GetV2EnvelopeStatuses({
                    programId: this.ProgramId,
                    projectId: this.ProjectId
                });
                this.v2Envelopes = result.Data.Envelopes;
                this.contentLibraryItemsById = result.Data.ContentLibraryItemsById;
            },
            onV2EnvelopeUpdated(envelope) {
                // Remove updated envelope from the table and then reinsert it to show updated state
                this.v2Envelopes = this.v2Envelopes.filter((e) => {
                    return e.EnvelopeId !== envelope.EnvelopeId;
                });
                this.v2Envelopes.push(envelope);
            },
            onV2EnvelopeVoided(envelope) {
                // Remove voided envelope from the table.
                this.v2Envelopes = this.v2Envelopes.filter((e) => {
                    return e.EnvelopeId !== envelope.EnvelopeId;
                });
            }
        }
    };
</script>
